import { useAsyncData } from '#imports'
import { computed, ref, watch, watchEffect } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'

import type {
  BuybackMessage,
  BuybackOrderMessage,
} from '@backmarket/nuxt-layer-buyback/api/conversation/types'
import {
  type Role,
  conversationEndpoints,
} from '@backmarket/nuxt-layer-buyback/components/OrderConversation/OrderConversation.constants'

import { conversationAdapter, detailsAdapter } from './adapters'

export function useConversation(role: Role, id: string) {
  const i18n = useI18n()
  const logger = useLogger()
  const locale = useI18nLocale()
  const { openErrorToast } = useTheToast()
  const $httpFetch = useHttpFetch()

  const pageInput = ref('1')
  const messageInput = ref<BuybackMessage>()
  const storedMessages = ref<BuybackOrderMessage[]>([])

  const userMessage = computed(() => {
    const body = new FormData()
    if (messageInput.value) {
      if (messageInput.value.message) {
        body.append('message', messageInput.value.message)
      }

      if (messageInput.value?.files && messageInput.value.files.length > 0) {
        messageInput.value.files.forEach((file) => {
          body.append('files', file)
        })
      }
    }

    return body
  })

  const {
    data: postMessageResponse,
    error: postMessageError,
    status: postMessageStatus,
  } = useAsyncData(
    async () => {
      return $httpFetch(conversationEndpoints[role].postMessage, {
        method: 'POST',
        body: userMessage.value,
        pathParams: { id },
      })
    },
    { immediate: false, watch: [userMessage] },
  )

  const {
    data: conversationResponse,
    error: conversationError,
    status: conversationStatus,
  } = useAsyncData(
    async () => {
      return $httpFetch(conversationEndpoints[role].getConversation, {
        pathParams: { id },
        query: { page: pageInput.value, pageSize: 20 },
      })
    },
    { watch: [pageInput] },
  )

  const {
    data: metadataResponse,
    error: metadataError,
    status: metadataStatus,
  } = useAsyncData(async () => {
    return $httpFetch(conversationEndpoints[role].getMetadata, {
      pathParams: { id },
    })
  })

  watch(conversationError, () => {
    if (!conversationError.value) return

    logger.error('[BUYBACK][API][CONVERSATION] Error getting conversation', {
      error: conversationError.value,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })
  })

  watch(metadataError, () => {
    if (!metadataError.value) return

    logger.error(
      '[BUYBACK][API][CONVERSATION] Error getting conversation metadata',
      {
        error: metadataError.value,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    )
  })

  watch(postMessageError, () => {
    if (!postMessageError.value) return

    logger.error(
      '[BUYBACK][API][CONVERSATION] Error posting conversation message',
      {
        error: postMessageError.value,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    )
  })

  const conversation = computed(() => {
    if (
      storedMessages.value.length === 0 &&
      conversationResponse.value?.results
    ) {
      return conversationAdapter(
        conversationResponse.value,
        conversationResponse.value.results,
      )
    }
    if (storedMessages.value.length > 0 && conversationResponse.value) {
      return conversationAdapter(
        conversationResponse.value,
        storedMessages.value,
      )
    }

    return undefined
  })

  const metadata = computed(() => {
    if (metadataResponse.value) {
      return detailsAdapter(metadataResponse.value, role, id, i18n, locale)
    }

    return undefined
  })

  const isLoadingPostMessage = computed(() => {
    return postMessageStatus.value === 'pending'
  })

  const isLoadingConversation = computed(() => {
    return conversationStatus.value === 'pending'
  })

  const isLoadingMetadata = computed(() => {
    return metadataStatus.value === 'pending'
  })

  const isLoadingMore = computed(() => !!conversation.value?.next)

  watchEffect(() => {
    if (conversationResponse.value?.results) {
      storedMessages.value.push(...conversationResponse.value.results)
    }
  })

  watchEffect(() => {
    if (
      conversationError.value ||
      metadataError.value ||
      postMessageError.value
    ) {
      openErrorToast()
    }
  })

  watchEffect(() => {
    if (postMessageResponse.value) {
      storedMessages.value.unshift(postMessageResponse.value)
    }
  })

  const loadMore = () => {
    if (conversation.value?.next) {
      pageInput.value = conversation.value.next
    }
  }

  return {
    conversation,
    metadata,
    isLoadingConversation,
    isLoadingMetadata,
    isLoadingPostMessage,
    isLoadingMore,
    messageInput,
    pageInput,
    loadMore,
  }
}
